import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function Terms() {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <h1>Terms of Service</h1>
        <div className="legal-updated">Last updated: July 20, 2026</div>
      </section>

      <div className="legal-container">
        <p className="legal-intro">
          These Terms of Service ("Terms") govern your access to and use of Alooma Network, a
          creator–brand marketplace operated by Alooma Media Group ("Alooma," "we," "us," or "our"),
          available at <a href="https://alooma.mg">alooma.mg</a> (the "Service"). By creating an account
          or using the Service, you agree to these Terms and to our{' '}
          <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not use the Service.
        </p>

        <nav className="legal-toc">
          <h2>Contents</h2>
          <ol>
            <li><a href="#eligibility">Eligibility &amp; Accounts</a></li>
            <li><a href="#service">The Service</a></li>
            <li><a href="#bids">Bids, Invitations &amp; Engagements</a></li>
            <li><a href="#payments">Payments</a></li>
            <li><a href="#social">Connecting Social Accounts</a></li>
            <li><a href="#acceptable-use">Acceptable Use</a></li>
            <li><a href="#content">Your Content</a></li>
            <li><a href="#ip">Intellectual Property</a></li>
            <li><a href="#third-party">Third-Party Services</a></li>
            <li><a href="#disclaimers">Disclaimers</a></li>
            <li><a href="#liability">Limitation of Liability</a></li>
            <li><a href="#indemnification">Indemnification</a></li>
            <li><a href="#termination">Termination</a></li>
            <li><a href="#governing-law">Governing Law</a></li>
            <li><a href="#changes">Changes to These Terms</a></li>
            <li><a href="#contact">Contact Us</a></li>
          </ol>
        </nav>

        <section className="legal-section" id="eligibility">
          <h2>1. Eligibility &amp; Accounts</h2>
          <p>
            You must be at least 18 years old and able to form a binding contract to use the Service. You
            are responsible for the accuracy of the information you provide, for maintaining the
            confidentiality of your login credentials, and for all activity under your account. Notify us
            immediately of any unauthorized use.
          </p>
        </section>

        <section className="legal-section" id="service">
          <h2>2. The Service</h2>
          <p>
            Alooma Network connects brands and content creators. Brands can post campaigns ("jobs") and
            invite or review creator proposals; creators can browse jobs, submit bids, showcase verified
            social metrics, and communicate with brands. Alooma provides the platform that facilitates
            these connections but is not a party to the agreements formed between brands and creators.
          </p>
        </section>

        <section className="legal-section" id="bids">
          <h2>3. Bids, Invitations &amp; Engagements</h2>
          <p>
            Bids, proposals, and invitations exchanged on the Service are offers between users. When a
            brand accepts a creator's bid, any resulting engagement is a direct agreement between those
            users. You are solely responsible for the terms, performance, deliverables, disclosures
            (including applicable advertising/FTC disclosure requirements), and outcomes of your
            engagements. Alooma does not guarantee any particular results, quality, payment, or conduct.
          </p>
        </section>

        <section className="legal-section" id="payments">
          <h2>4. Payments</h2>
          <p>
            Unless expressly stated otherwise in writing, Alooma does not currently process or escrow
            payments between brands and creators. Payment terms, invoicing, and settlement are arranged
            directly between the parties to an engagement. You are responsible for your own taxes and for
            complying with applicable financial and contractual obligations.
          </p>
        </section>

        <section className="legal-section" id="social">
          <h2>5. Connecting Social Accounts</h2>
          <p>
            Creators may connect third-party social accounts (e.g., Instagram, Facebook, TikTok, YouTube,
            X, Snapchat) via official OAuth. By connecting an account, you represent that you own or are
            authorized to use it, and you authorize Alooma to retrieve and display the metrics described in
            our <Link to="/privacy">Privacy Policy</Link>. Your use of each platform remains subject to
            that platform's own terms. You may disconnect at any time.
          </p>
        </section>

        <section className="legal-section" id="acceptable-use">
          <h2>6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Provide false, misleading, or fraudulent information, including fake metrics or impersonation.</li>
            <li>Post unlawful, infringing, harassing, hateful, or sexually exploitative content.</li>
            <li>Circumvent, spam, scrape, reverse engineer, or disrupt the Service or its security.</li>
            <li>Use the Service to transmit malware or to violate the rights of others.</li>
            <li>Solicit users for purposes unrelated to legitimate brand–creator collaboration.</li>
          </ul>
        </section>

        <section className="legal-section" id="content">
          <h2>7. Your Content</h2>
          <p>
            You retain ownership of the content you submit (profiles, messages, proposals, media). You
            grant Alooma a non-exclusive, worldwide, royalty-free license to host, store, display, and use
            that content as necessary to operate and provide the Service. You represent that you have the
            rights necessary to grant this license.
          </p>
        </section>

        <section className="legal-section" id="ip">
          <h2>8. Intellectual Property</h2>
          <p>
            The Service, including its software, design, and trademarks, is owned by Alooma and protected
            by intellectual property laws. Except as expressly permitted, you may not copy, modify,
            distribute, or create derivative works from the Service.
          </p>
        </section>

        <section className="legal-section" id="third-party">
          <h2>9. Third-Party Services</h2>
          <p>
            The Service integrates with third-party platforms and providers. We are not responsible for
            the availability, content, or practices of third parties, and your use of them is governed by
            their respective terms.
          </p>
        </section>

        <section className="legal-section" id="disclaimers">
          <h2>10. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER
            EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, secure, or
            error-free, or that any engagement facilitated through it will meet your expectations.
          </p>
        </section>

        <section className="legal-section" id="liability">
          <h2>11. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALOOMA AND ITS AFFILIATES WILL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
            REVENUE, DATA, OR GOODWILL, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL
            LIABILITY FOR ANY CLAIM WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS ($100).
          </p>
        </section>

        <section className="legal-section" id="indemnification">
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Alooma and its affiliates from any claims, damages,
            liabilities, and expenses (including reasonable legal fees) arising out of your use of the
            Service, your content, your engagements with other users, or your violation of these Terms or
            applicable law.
          </p>
        </section>

        <section className="legal-section" id="termination">
          <h2>13. Termination</h2>
          <p>
            You may stop using the Service and request account deletion at any time. We may suspend or
            terminate your access if you violate these Terms or if necessary to protect the Service or its
            users. Provisions that by their nature should survive termination will survive.
          </p>
        </section>

        <section className="legal-section" id="governing-law">
          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of New York, United States, without regard to
            its conflict-of-laws rules. You agree to the exclusive jurisdiction of the state and federal
            courts located in New York for any disputes not subject to alternative resolution.
          </p>
        </section>

        <section className="legal-section" id="changes">
          <h2>15. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. The revised version will be posted here with an
            updated date, and continued use of the Service constitutes acceptance of the changes.
          </p>
        </section>

        <section className="legal-section" id="contact">
          <h2>16. Contact Us</h2>
          <p>
            Questions about these Terms can be sent to{' '}
            <a href="mailto:info@aloomamediagroup.com">info@aloomamediagroup.com</a>.
          </p>
        </section>
      </div>

      <footer className="legal-footer">
        <p>©2026 Alooma Media Group. All rights reserved.</p>
        <p>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/">Home</Link>
        </p>
      </footer>
    </div>
  );
}

export default Terms;
